'use client';

import { useState } from 'react';
import ToolHero from '@/components/tools/ToolHero';
import ToolCTA from '@/components/tools/ToolCTA';
import CopyButton from '@/components/tools/CopyButton';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type Tone = 'Professional' | 'Conversational' | 'Bold';
type Perspective = 'First' | 'Third';
type SummaryStyle = 'Storyteller' | 'Achievement-Led' | 'Value Proposition';

interface GeneratedSummary {
  style: SummaryStyle;
  description: string;
  text: string;
}

interface FormData {
  role: string;
  industry: string;
  yearsOfExperience: string;
  skills: string;
  achievement: string;
  careerGoals: string;
  tone: Tone;
  perspective: Perspective;
}

/* ------------------------------------------------------------------ */
/*  Utility helpers                                                    */
/* ------------------------------------------------------------------ */

function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function parseSkills(raw: string): string[] {
  return raw
    .split(/[,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function formatSkillsList(skills: string[]): string {
  if (skills.length === 0) return '';
  if (skills.length === 1) return skills[0];
  if (skills.length === 2) return `${skills[0]} and ${skills[1]}`;
  return `${skills.slice(0, -1).join(', ')}, and ${skills[skills.length - 1]}`;
}

function yearsLabel(years: string): string {
  const n = parseInt(years, 10);
  if (isNaN(n) || n <= 0) return '';
  if (n === 1) return '1 year';
  return `${n} years`;
}

function yearsPlus(years: string): string {
  const n = parseInt(years, 10);
  if (isNaN(n) || n <= 0) return '';
  return `${n}+`;
}

/** Return the correct subject pronoun set for the chosen perspective. */
function pronouns(perspective: Perspective, role: string) {
  if (perspective === 'First') {
    return { I: 'I', my: 'my', me: 'me', ive: "I've", im: "I'm", myself: 'myself', iAm: 'I am', iHave: 'I have' };
  }
  // Third person - use the role or a generic noun
  const name = role || 'This professional';
  return {
    I: name,
    my: `${name}'s`,
    me: name.toLowerCase(),
    ive: `${name} has`,
    im: `${name} is`,
    myself: name.toLowerCase(),
    iAm: `${name} is`,
    iHave: `${name} has`,
  };
}

/* ------------------------------------------------------------------ */
/*  Industry-specific language banks                                   */
/* ------------------------------------------------------------------ */

const industryContext: Record<string, {
  domains: string[];
  impacts: string[];
  verbs: string[];
  buzzwords: string[];
}> = {
  Technology: {
    domains: ['software development', 'digital innovation', 'technology solutions', 'product engineering', 'cloud infrastructure'],
    impacts: ['scalable solutions', 'system reliability', 'product adoption', 'technical architecture', 'engineering velocity'],
    verbs: ['architected', 'engineered', 'deployed', 'automated', 'optimized'],
    buzzwords: ['digital transformation', 'AI-driven', 'cloud-native', 'data-driven decisions', 'developer experience'],
  },
  Finance: {
    domains: ['financial services', 'capital markets', 'risk management', 'financial operations', 'investment strategy'],
    impacts: ['portfolio performance', 'regulatory compliance', 'revenue growth', 'risk mitigation', 'cost optimization'],
    verbs: ['managed', 'analyzed', 'forecasted', 'restructured', 'audited'],
    buzzwords: ['fiduciary responsibility', 'market volatility', 'quantitative analysis', 'compliance frameworks', 'financial modeling'],
  },
  Healthcare: {
    domains: ['patient care', 'clinical operations', 'health systems', 'medical research', 'healthcare delivery'],
    impacts: ['patient outcomes', 'care quality', 'operational efficiency', 'clinical workflows', 'compliance standards'],
    verbs: ['improved', 'coordinated', 'implemented', 'streamlined', 'standardized'],
    buzzwords: ['evidence-based practice', 'value-based care', 'population health', 'clinical excellence', 'health equity'],
  },
  Marketing: {
    domains: ['brand strategy', 'digital marketing', 'market positioning', 'campaign management', 'growth marketing'],
    impacts: ['brand awareness', 'lead generation', 'conversion rates', 'customer engagement', 'market share'],
    verbs: ['launched', 'drove', 'scaled', 'positioned', 'amplified'],
    buzzwords: ['omnichannel strategy', 'content-led growth', 'brand storytelling', 'customer lifetime value', 'demand generation'],
  },
  Education: {
    domains: ['curriculum development', 'academic programs', 'educational technology', 'student success', 'learning design'],
    impacts: ['student outcomes', 'program effectiveness', 'learning engagement', 'curriculum quality', 'institutional goals'],
    verbs: ['developed', 'mentored', 'designed', 'facilitated', 'assessed'],
    buzzwords: ['personalized learning', 'inclusive pedagogy', 'outcomes-based education', 'lifelong learning', 'academic innovation'],
  },
  Sales: {
    domains: ['business development', 'revenue generation', 'client relationships', 'sales strategy', 'account management'],
    impacts: ['revenue targets', 'pipeline growth', 'client retention', 'deal closure', 'market expansion'],
    verbs: ['closed', 'negotiated', 'cultivated', 'exceeded', 'secured'],
    buzzwords: ['consultative selling', 'solution selling', 'strategic partnerships', 'territory growth', 'customer success'],
  },
  Engineering: {
    domains: ['systems engineering', 'product development', 'technical design', 'infrastructure', 'process engineering'],
    impacts: ['system performance', 'design efficiency', 'project delivery', 'quality standards', 'operational uptime'],
    verbs: ['designed', 'built', 'tested', 'integrated', 'validated'],
    buzzwords: ['lean manufacturing', 'continuous improvement', 'design thinking', 'systems optimization', 'cross-functional collaboration'],
  },
  Design: {
    domains: ['user experience', 'visual design', 'product design', 'design systems', 'creative direction'],
    impacts: ['user satisfaction', 'design consistency', 'conversion optimization', 'brand identity', 'usability metrics'],
    verbs: ['crafted', 'designed', 'prototyped', 'iterated', 'conceptualized'],
    buzzwords: ['human-centered design', 'design systems', 'rapid prototyping', 'user research', 'pixel-perfect execution'],
  },
  Consulting: {
    domains: ['strategic consulting', 'management advisory', 'operational improvement', 'change management', 'business transformation'],
    impacts: ['client outcomes', 'process efficiency', 'strategic alignment', 'organizational agility', 'stakeholder buy-in'],
    verbs: ['advised', 'transformed', 'facilitated', 'diagnosed', 'recommended'],
    buzzwords: ['change management', 'stakeholder alignment', 'business case development', 'operational excellence', 'thought leadership'],
  },
  Other: {
    domains: ['cross-functional initiatives', 'operational excellence', 'strategic planning', 'process improvement', 'project management'],
    impacts: ['organizational goals', 'team productivity', 'process efficiency', 'stakeholder satisfaction', 'business outcomes'],
    verbs: ['led', 'managed', 'delivered', 'drove', 'spearheaded'],
    buzzwords: ['continuous improvement', 'cross-functional leadership', 'strategic execution', 'stakeholder management', 'operational agility'],
  },
};

/* ------------------------------------------------------------------ */
/*  Tone modifiers                                                     */
/* ------------------------------------------------------------------ */

const toneModifiers: Record<Tone, {
  connectors: string[];
  emphatics: string[];
  closers: string[];
}> = {
  Professional: {
    connectors: ['Furthermore,', 'Additionally,', 'Moreover,', 'In addition,', 'Equally important,'],
    emphatics: ['significant', 'substantial', 'measurable', 'meaningful', 'tangible'],
    closers: [
      'Open to connecting with professionals who share a commitment to excellence.',
      'Always interested in meaningful conversations about industry challenges and opportunities.',
      'Welcome the opportunity to discuss how mutual goals can be advanced.',
      'Keen to explore new opportunities that align with long-term professional growth.',
      'Interested in connecting with like-minded professionals and thought leaders.',
    ],
  },
  Conversational: {
    connectors: ['But here is the thing --', 'And honestly,', 'What really drives me is', 'The best part?', 'Here is what excites me:'],
    emphatics: ['genuinely exciting', 'incredibly rewarding', 'really impactful', 'pretty remarkable', 'truly fulfilling'],
    closers: [
      'If any of this resonates, let us grab a virtual coffee and chat.',
      'Always happy to connect -- drop me a message anytime.',
      'Love meeting people who are passionate about this space. Let us talk.',
      'If this sounds like your world too, I would love to hear from you.',
      'Always up for a good conversation about where the industry is headed.',
    ],
  },
  Bold: {
    connectors: ['Here is the truth:', 'Let me be direct:', 'The reality?', 'No sugarcoating:', 'Bottom line:'],
    emphatics: ['game-changing', 'breakthrough', 'unmatched', 'exceptional', 'extraordinary'],
    closers: [
      'Ready to bring this same energy to the right opportunity. Let us make it happen.',
      'Not here to play small. Let us build something exceptional together.',
      'Looking for the next big challenge. If that is you, let us connect.',
      'The best work happens when ambition meets opportunity. Let us talk.',
      'Done settling for good enough. The next chapter will be the best one yet.',
    ],
  },
};

/* ------------------------------------------------------------------ */
/*  Level-appropriate language (inferred from years of experience)      */
/* ------------------------------------------------------------------ */

function inferLevel(years: string): 'entry' | 'mid' | 'senior' | 'executive' {
  const n = parseInt(years, 10);
  if (isNaN(n) || n <= 2) return 'entry';
  if (n <= 6) return 'mid';
  if (n <= 14) return 'senior';
  return 'executive';
}

const levelLanguage: Record<string, {
  descriptors: string[];
  openers: string[];
}> = {
  entry: {
    descriptors: ['motivated', 'curious', 'driven', 'eager', 'passionate'],
    openers: ['Early in my career', 'Just getting started and already making waves', 'New to the professional world but not to hard work', 'Fresh perspective meets genuine dedication', 'Bringing fresh energy'],
  },
  mid: {
    descriptors: ['experienced', 'versatile', 'results-oriented', 'resourceful', 'collaborative'],
    openers: ['Several years in and more excited than ever', 'Deep enough to know the nuances', 'Past the learning curve and hitting my stride', 'Battle-tested and hungry for more', 'In the sweet spot of experience and ambition'],
  },
  senior: {
    descriptors: ['strategic', 'seasoned', 'high-impact', 'forward-thinking', 'influential'],
    openers: ['After years of building and leading', 'With a decade-plus of real-world experience', 'Having navigated both growth and adversity', 'Standing at the intersection of expertise and vision', 'With the scars and wins to prove it'],
  },
  executive: {
    descriptors: ['visionary', 'transformational', 'enterprise-minded', 'executive-level', 'globally experienced'],
    openers: ['Across two decades of leadership', 'Having built and scaled organizations', 'From boardroom strategy to frontline execution', 'With a career defined by transformation', 'At the helm of some of the most challenging initiatives'],
  },
};

/* ------------------------------------------------------------------ */
/*  Storyteller style (5+ variations)                                  */
/* ------------------------------------------------------------------ */

function generateStoryteller(data: FormData): string {
  const { role, industry, yearsOfExperience, skills, achievement, careerGoals, tone, perspective } = data;
  const p = pronouns(perspective, role);
  const sk = parseSkills(skills);
  const indCtx = industryContext[industry] || industryContext.Other;
  const tMod = toneModifiers[tone];
  const level = inferLevel(yearsOfExperience);
  const lvl = levelLanguage[level];
  const yrs = yearsLabel(yearsOfExperience);
  const domain = pickRandom(indCtx.domains);
  const impact = pickRandom(indCtx.impacts);
  const buzz = pickRandom(indCtx.buzzwords);
  const descriptor = pickRandom(lvl.descriptors);
  const opener = pickRandom(lvl.openers);
  const connector = pickRandom(tMod.connectors);
  const emphatic = pickRandom(tMod.emphatics);
  const closer = perspective === 'First' ? pickRandom(tMod.closers) : pickRandom(tMod.closers).replace(/\bI\b/g, p.I).replace(/\bme\b/g, p.me).replace(/\bmy\b/g, p.my);

  const skillsPhrase = sk.length > 0 ? formatSkillsList(sk) : domain;
  const achievePhrase = achievement.trim()
    ? achievement.trim().replace(/[.!]+$/, '')
    : '';
  const goalsPhrase = careerGoals.trim() || '';

  const templates = [
    // Template 1: Classic narrative arc
    () => {
      const hook = yrs
        ? `${opener} -- ${yrs} ago, ${p.I} stepped into the world of ${industry} with nothing but curiosity and determination.`
        : `${opener} -- ${p.I} stepped into the world of ${industry} with nothing but curiosity and determination.`;
      const body = `Today, ${p.iAm} a ${descriptor} ${role} who has turned that early drive into ${emphatic} results across ${domain}. ${p.my.charAt(0).toUpperCase() + p.my.slice(1)} expertise in ${skillsPhrase} did not come from textbooks alone -- it was forged through real challenges, tight deadlines, and the relentless pursuit of ${impact}.`;
      const achieveLine = achievePhrase
        ? `\n\n${connector} one of ${p.my} proudest moments was ${achievePhrase.charAt(0).toLowerCase() + achievePhrase.slice(1)}. That experience taught ${p.me} that the right combination of skill and persistence can move mountains.`
        : `\n\n${connector} what keeps ${p.me} going is the belief that every project is a chance to create something ${emphatic} -- something that genuinely moves the needle on ${impact}.`;
      const goalLine = goalsPhrase
        ? `\n\nLooking ahead, ${p.iAm} focused on ${goalsPhrase.charAt(0).toLowerCase() + goalsPhrase.slice(1)}. ${closer}`
        : `\n\n${closer}`;
      return hook + '\n\n' + body + achieveLine + goalLine;
    },

    // Template 2: The "aha moment" narrative
    () => {
      const hook = `There was a moment early in ${p.my} ${industry} career when everything clicked: ${buzz} was not just a buzzword -- it was the key to unlocking ${emphatic} change.`;
      const body = yrs
        ? `That realization has fueled ${yrs} of work as a ${role}, building expertise in ${skillsPhrase} and consistently delivering on ${impact}. ${p.I} ${pickRandom(indCtx.verbs)} solutions that balanced ambition with practicality, always keeping the bigger picture in focus.`
        : `That realization has fueled ${p.my} entire career as a ${role}, building expertise in ${skillsPhrase} and consistently delivering on ${impact}. ${p.I} ${pickRandom(indCtx.verbs)} solutions that balanced ambition with practicality.`;
      const achieveLine = achievePhrase
        ? `\n\n${connector} ${achievePhrase}. It was a defining moment that reinforced ${p.my} belief in combining ${descriptor} thinking with hands-on execution.`
        : `\n\n${connector} ${p.I} thrive at the intersection of strategy and execution -- where ${descriptor} thinking meets real-world problem solving.`;
      const goalLine = goalsPhrase
        ? `\n\nNow, ${p.iAm} channeling that same energy into ${goalsPhrase.charAt(0).toLowerCase() + goalsPhrase.slice(1)}. ${closer}`
        : `\n\n${closer}`;
      return hook + '\n\n' + body + achieveLine + goalLine;
    },

    // Template 3: Problem-solver journey
    () => {
      const hook = `Every great ${role} has a superpower. ${p.my.charAt(0).toUpperCase() + p.my.slice(1)}? Turning ${pickRandom(indCtx.domains)} challenges into ${emphatic} outcomes.`;
      const body = yrs
        ? `Over ${yrs} in ${industry}, ${p.ive} developed a ${descriptor} approach to ${domain}, grounded in ${skillsPhrase}. ${p.I} do not just solve problems -- ${p.I} anticipate them, designing systems and strategies that drive ${impact} before issues even surface.`
        : `Throughout ${p.my} career in ${industry}, ${p.ive} developed a ${descriptor} approach to ${domain}, grounded in ${skillsPhrase}. ${p.I} do not just solve problems -- ${p.I} anticipate them.`;
      const achieveLine = achievePhrase
        ? `\n\nCase in point: ${achievePhrase}. That kind of ${emphatic} result does not happen by accident -- it is the product of deep expertise, collaboration, and a bias toward action.`
        : `\n\nThis approach has consistently produced ${emphatic} results -- from improving ${impact} to building teams that punch above their weight.`;
      const goalLine = goalsPhrase
        ? `\n\nWhat is next? ${p.iAm} looking to ${goalsPhrase.charAt(0).toLowerCase() + goalsPhrase.slice(1)}. ${closer}`
        : `\n\n${closer}`;
      return hook + '\n\n' + body + achieveLine + goalLine;
    },

    // Template 4: The evolution story
    () => {
      const hook = yrs
        ? `${yrs.charAt(0).toUpperCase() + yrs.slice(1)} ago, ${p.I} started in ${industry} with a simple goal: master the fundamentals and build from there.`
        : `When ${p.I} first entered ${industry}, the goal was simple: master the fundamentals and build from there.`;
      const body = `Fast forward to today, and ${p.iAm} a ${descriptor} ${role} who has evolved far beyond those early ambitions. ${p.my.charAt(0).toUpperCase() + p.my.slice(1)} journey has taken ${p.me} deep into ${skillsPhrase}, where ${p.ive} discovered that the most ${emphatic} results come from combining technical excellence with genuine human connection.`;
      const achieveLine = achievePhrase
        ? `\n\nAlong the way, ${p.I} ${achievePhrase.charAt(0).toLowerCase() + achievePhrase.slice(1)}. Moments like that remind ${p.me} why ${p.I} chose this path.`
        : `\n\n${connector} the work that matters most is the work that elevates others. ${p.I} take pride in building teams, mentoring peers, and creating environments where ${impact} becomes the norm rather than the exception.`;
      const goalLine = goalsPhrase
        ? `\n\nThe next chapter is about ${goalsPhrase.charAt(0).toLowerCase() + goalsPhrase.slice(1)}. ${closer}`
        : `\n\n${closer}`;
      return hook + '\n\n' + body + achieveLine + goalLine;
    },

    // Template 5: The passion-driven story
    () => {
      const hook = `Some people stumble into their career. ${p.I} chased ${p.my} into ${industry} because ${p.I} genuinely believe that great ${domain} can change how people work, live, and think.`;
      const body = `As a ${role}${yrs ? ` with ${yrs} of experience` : ''}, ${p.ive} built a career around ${skillsPhrase} -- not because it looks good on paper, but because it is where ${p.I} do ${p.my} most ${emphatic} work. ${p.I} thrive in environments that value ${buzz} and are not afraid to challenge the status quo.`;
      const achieveLine = achievePhrase
        ? `\n\n${connector} that mindset has delivered real results. ${achievePhrase.charAt(0).toUpperCase() + achievePhrase.slice(1)} -- proof that passion and execution are not mutually exclusive.`
        : `\n\n${connector} this mindset has consistently translated into ${emphatic} outcomes: stronger ${impact}, better collaboration, and teams that actually enjoy the work.`;
      const goalLine = goalsPhrase
        ? `\n\nRight now, ${p.iAm} focused on ${goalsPhrase.charAt(0).toLowerCase() + goalsPhrase.slice(1)}. ${closer}`
        : `\n\n${closer}`;
      return hook + '\n\n' + body + achieveLine + goalLine;
    },

    // Template 6: The contrast hook
    () => {
      const hook = `Most ${role}s focus on ${pickRandom(indCtx.domains)}. ${p.I} focus on what happens after -- the ${emphatic} outcomes that actually move a business forward.`;
      const body = yrs
        ? `With ${yrs} in ${industry}, ${p.ive} learned that the difference between good and exceptional lies in ${skillsPhrase}. ${p.my.charAt(0).toUpperCase() + p.my.slice(1)} ${descriptor} approach to ${domain} has consistently delivered improvements in ${impact} that stakeholders can feel.`
        : `In ${p.my} time in ${industry}, ${p.ive} learned that the difference between good and exceptional lies in ${skillsPhrase}. ${p.my.charAt(0).toUpperCase() + p.my.slice(1)} ${descriptor} approach to ${domain} has consistently delivered improvements in ${impact}.`;
      const achieveLine = achievePhrase
        ? `\n\n${achievePhrase.charAt(0).toUpperCase() + achievePhrase.slice(1)} -- and that is just one chapter of a career built on turning ambition into action.`
        : `\n\n${p.I} believe in doing work that compounds -- where each project makes the next one stronger, and where ${impact} is not a one-time event but a consistent pattern.`;
      const goalLine = goalsPhrase
        ? `\n\nCurrently seeking opportunities to ${goalsPhrase.charAt(0).toLowerCase() + goalsPhrase.slice(1)}. ${closer}`
        : `\n\n${closer}`;
      return hook + '\n\n' + body + achieveLine + goalLine;
    },
  ];

  return pickRandom(templates)();
}

/* ------------------------------------------------------------------ */
/*  Achievement-Led style (5+ variations)                              */
/* ------------------------------------------------------------------ */

function generateAchievementLed(data: FormData): string {
  const { role, industry, yearsOfExperience, skills, achievement, careerGoals, tone, perspective } = data;
  const p = pronouns(perspective, role);
  const sk = parseSkills(skills);
  const indCtx = industryContext[industry] || industryContext.Other;
  const tMod = toneModifiers[tone];
  const level = inferLevel(yearsOfExperience);
  const lvl = levelLanguage[level];
  const yrs = yearsLabel(yearsOfExperience);
  const domain = pickRandom(indCtx.domains);
  const impact = pickRandom(indCtx.impacts);
  const impact2 = pickRandom(indCtx.impacts);
  const descriptor = pickRandom(lvl.descriptors);
  const emphatic = pickRandom(tMod.emphatics);
  const connector = pickRandom(tMod.connectors);
  const closer = perspective === 'First' ? pickRandom(tMod.closers) : pickRandom(tMod.closers).replace(/\bI\b/g, p.I).replace(/\bme\b/g, p.me).replace(/\bmy\b/g, p.my);

  const skillsPhrase = sk.length > 0 ? formatSkillsList(sk) : domain;
  const achievePhrase = achievement.trim()
    ? achievement.trim().replace(/[.!]+$/, '')
    : '';
  const goalsPhrase = careerGoals.trim() || '';

  const templates = [
    // Template 1: Lead with the biggest number
    () => {
      const achieveOpener = achievePhrase
        ? `${achievePhrase.charAt(0).toUpperCase() + achievePhrase.slice(1)}.`
        : `Consistently delivered ${emphatic} improvements in ${impact} across every role.`;
      const body = yrs
        ? `That is the kind of result ${p.I} deliver as a ${descriptor} ${role} with ${yrs} of experience in ${industry}. ${p.my.charAt(0).toUpperCase() + p.my.slice(1)} expertise spans ${skillsPhrase}, and ${p.I} apply these skills with a singular focus: driving ${impact2} that stakeholders can measure.`
        : `That is the kind of result ${p.I} deliver as a ${descriptor} ${role} in ${industry}. ${p.my.charAt(0).toUpperCase() + p.my.slice(1)} expertise spans ${skillsPhrase}, applied with a singular focus on driving ${impact2} that stakeholders can measure.`;
      const middle = `\n\n${connector} numbers only tell part of the story. Behind every metric is a team ${p.I} helped build, a process ${p.I} ${pickRandom(indCtx.verbs)}, or a strategy ${p.I} championed from concept to execution.`;
      const goalLine = goalsPhrase
        ? `\n\nNow focused on ${goalsPhrase.charAt(0).toLowerCase() + goalsPhrase.slice(1)}. ${closer}`
        : `\n\n${closer}`;
      return achieveOpener + '\n\n' + body + middle + goalLine;
    },

    // Template 2: Results portfolio
    () => {
      const opener = yrs
        ? `In ${yrs} as a ${role} in ${industry}, ${p.ive} built a track record that speaks for itself:`
        : `As a ${role} in ${industry}, ${p.ive} built a track record that speaks for itself:`;
      const bullet1 = achievePhrase
        ? achievePhrase.charAt(0).toUpperCase() + achievePhrase.slice(1)
        : `Delivered ${emphatic} improvements in ${impact}`;
      const bullet2 = `${pickRandom(indCtx.verbs).charAt(0).toUpperCase() + pickRandom(indCtx.verbs).slice(1)} solutions that enhanced ${impact2} across ${domain}`;
      const bullet3 = sk.length >= 2
        ? `Applied expertise in ${sk[0]} and ${sk[1]} to solve high-stakes problems`
        : `Applied ${descriptor} thinking to solve high-stakes ${industry} challenges`;
      const body = `\n\n- ${bullet1}\n- ${bullet2}\n- ${bullet3}`;
      const narrative = `\n\nThese results are rooted in ${p.my} ${descriptor} approach to ${domain}. ${p.I} believe that ${emphatic} outcomes are not accidents -- they are the product of clear strategy, deep expertise in ${skillsPhrase}, and relentless follow-through.`;
      const goalLine = goalsPhrase
        ? `\n\n${p.iAm.charAt(0).toUpperCase() + p.iAm.slice(1)} now looking to ${goalsPhrase.charAt(0).toLowerCase() + goalsPhrase.slice(1)}. ${closer}`
        : `\n\n${closer}`;
      return opener + body + narrative + goalLine;
    },

    // Template 3: Before/after framing
    () => {
      const hook = achievePhrase
        ? `When ${p.I} ${pickRandom(['joined', 'stepped into', 'took on'])} ${p.my} most recent challenge, the goal was clear. The result? ${achievePhrase.charAt(0).toUpperCase() + achievePhrase.slice(1)}.`
        : `Every role ${p.ive} held has followed the same arc: identify the gap, build the strategy, deliver ${emphatic} results.`;
      const body = yrs
        ? `With ${yrs} of experience as a ${role} in ${industry}, ${p.I} specialize in turning ambitious targets into measurable wins. ${p.my.charAt(0).toUpperCase() + p.my.slice(1)} core toolkit -- ${skillsPhrase} -- enables ${p.me} to ${pickRandom(indCtx.verbs)} initiatives that directly improve ${impact} and ${impact2}.`
        : `As a ${role} in ${industry}, ${p.I} specialize in turning ambitious targets into measurable wins. ${p.my.charAt(0).toUpperCase() + p.my.slice(1)} core toolkit -- ${skillsPhrase} -- enables ${p.me} to ${pickRandom(indCtx.verbs)} initiatives that directly improve ${impact}.`;
      const middle = `\n\n${connector} ${p.I} do not just hit targets -- ${p.I} raise the bar. ${p.my.charAt(0).toUpperCase() + p.my.slice(1)} ${descriptor} approach ensures that improvements are sustainable, scalable, and aligned with the broader business vision.`;
      const goalLine = goalsPhrase
        ? `\n\nCurrently pursuing ${goalsPhrase.charAt(0).toLowerCase() + goalsPhrase.slice(1)}. ${closer}`
        : `\n\n${closer}`;
      return hook + '\n\n' + body + middle + goalLine;
    },

    // Template 4: Metrics-first narrative
    () => {
      const verb = pickRandom(indCtx.verbs);
      const opener = achievePhrase
        ? `${achievePhrase.charAt(0).toUpperCase() + achievePhrase.slice(1)} -- and that is just one line on the scorecard.`
        : `${pickRandom(tMod.emphatics).charAt(0).toUpperCase() + pickRandom(tMod.emphatics).slice(1)} ${impact}. Stronger ${impact2}. Measurable, repeatable, scalable results.`;
      const body = yrs
        ? `As a ${descriptor} ${role} with ${yrs} in ${industry}, ${p.I} have made it ${p.my} mission to deliver outcomes that matter. ${p.my.charAt(0).toUpperCase() + p.my.slice(1)} approach combines deep knowledge of ${skillsPhrase} with a relentless focus on execution.`
        : `As a ${descriptor} ${role} in ${industry}, ${p.I} have made it ${p.my} mission to deliver outcomes that matter. ${p.my.charAt(0).toUpperCase() + p.my.slice(1)} approach combines deep knowledge of ${skillsPhrase} with a relentless focus on execution.`;
      const middle = `\n\n${p.I} have ${verb} solutions across ${domain}, always measuring success not by effort expended but by the ${emphatic} impact delivered. In a world of vanity metrics, ${p.I} focus on the numbers that actually move the business forward.`;
      const goalLine = goalsPhrase
        ? `\n\nNext up: ${goalsPhrase.charAt(0).toLowerCase() + goalsPhrase.slice(1)}. ${closer}`
        : `\n\n${closer}`;
      return opener + '\n\n' + body + middle + goalLine;
    },

    // Template 5: The track record story
    () => {
      const opener = `If there is one thing ${p.my} career proves, it is this: ${descriptor} thinking plus disciplined execution equals ${emphatic} results.`;
      const body = yrs
        ? `Over ${yrs} as a ${role} in ${industry}, ${p.ive} consistently ${pickRandom(indCtx.verbs)} initiatives that raised the bar on ${impact}. ${p.my.charAt(0).toUpperCase() + p.my.slice(1)} expertise in ${skillsPhrase} has been the foundation for every win.`
        : `As a ${role} in ${industry}, ${p.ive} consistently ${pickRandom(indCtx.verbs)} initiatives that raised the bar on ${impact}. ${p.my.charAt(0).toUpperCase() + p.my.slice(1)} expertise in ${skillsPhrase} has been the foundation.`;
      const achieveLine = achievePhrase
        ? `\n\n${connector} the proof is in the numbers. ${achievePhrase.charAt(0).toUpperCase() + achievePhrase.slice(1)} -- the kind of ${emphatic} outcome that comes from understanding both the strategy and the details.`
        : `\n\n${connector} ${p.I} have built a reputation for delivering not just results, but ${emphatic} results that compound over time. From ${impact} to ${impact2}, the pattern is consistent: set an ambitious target and exceed it.`;
      const goalLine = goalsPhrase
        ? `\n\n${p.iAm.charAt(0).toUpperCase() + p.iAm.slice(1)} ready for ${goalsPhrase.charAt(0).toLowerCase() + goalsPhrase.slice(1)}. ${closer}`
        : `\n\n${closer}`;
      return opener + '\n\n' + body + achieveLine + goalLine;
    },

    // Template 6: Direct impact opener
    () => {
      const verb = pickRandom(indCtx.verbs);
      const opener = achievePhrase
        ? `Results speak louder than resumes. Here is what ${p.my} says: ${achievePhrase.charAt(0).toLowerCase() + achievePhrase.slice(1)}.`
        : `Results speak louder than resumes. ${p.my.charAt(0).toUpperCase() + p.my.slice(1)} career in ${industry} is defined by ${emphatic} outcomes in ${impact} and ${impact2}.`;
      const body = yrs
        ? `${p.iAm.charAt(0).toUpperCase() + p.iAm.slice(1)} a ${role} with ${yrs} of experience, and ${p.I} bring a ${descriptor} mindset to everything ${p.I} touch. Core competencies in ${skillsPhrase} allow ${p.me} to ${verb} solutions that bridge the gap between ambition and reality.`
        : `${p.iAm.charAt(0).toUpperCase() + p.iAm.slice(1)} a ${role} who brings a ${descriptor} mindset to everything. Core competencies in ${skillsPhrase} allow ${p.me} to ${verb} solutions that bridge the gap between ambition and reality.`;
      const middle = `\n\n${connector} what separates ${p.me} is not just the ability to deliver, but the ability to sustain and scale that impact across ${domain}.`;
      const goalLine = goalsPhrase
        ? `\n\nLooking to ${goalsPhrase.charAt(0).toLowerCase() + goalsPhrase.slice(1)}. ${closer}`
        : `\n\n${closer}`;
      return opener + '\n\n' + body + middle + goalLine;
    },
  ];

  return pickRandom(templates)();
}

/* ------------------------------------------------------------------ */
/*  Value Proposition style (5+ variations)                            */
/* ------------------------------------------------------------------ */

function generateValueProposition(data: FormData): string {
  const { role, industry, yearsOfExperience, skills, achievement, careerGoals, tone, perspective } = data;
  const p = pronouns(perspective, role);
  const sk = parseSkills(skills);
  const indCtx = industryContext[industry] || industryContext.Other;
  const tMod = toneModifiers[tone];
  const level = inferLevel(yearsOfExperience);
  const lvl = levelLanguage[level];
  const yrs = yearsLabel(yearsOfExperience);
  const domain = pickRandom(indCtx.domains);
  const impact = pickRandom(indCtx.impacts);
  const impact2 = pickRandom(indCtx.impacts);
  const buzz = pickRandom(indCtx.buzzwords);
  const descriptor = pickRandom(lvl.descriptors);
  const emphatic = pickRandom(tMod.emphatics);
  const connector = pickRandom(tMod.connectors);
  const closer = perspective === 'First' ? pickRandom(tMod.closers) : pickRandom(tMod.closers).replace(/\bI\b/g, p.I).replace(/\bme\b/g, p.me).replace(/\bmy\b/g, p.my);

  const skillsPhrase = sk.length > 0 ? formatSkillsList(sk) : domain;
  const achievePhrase = achievement.trim()
    ? achievement.trim().replace(/[.!]+$/, '')
    : '';
  const goalsPhrase = careerGoals.trim() || '';

  const templates = [
    // Template 1: What I bring to the table
    () => {
      const hook = `Here is what ${p.I} bring to the table: ${emphatic} expertise in ${skillsPhrase}, a ${descriptor} approach to ${domain}, and a proven ability to drive ${impact}.`;
      const body = yrs
        ? `With ${yrs} in ${industry}, ${p.ive} learned that the best ${role}s do not just execute -- they elevate. ${p.I} help organizations navigate ${buzz} by combining deep technical skill with clear, strategic thinking.`
        : `As a ${role} in ${industry}, ${p.ive} learned that the best professionals do not just execute -- they elevate. ${p.I} help organizations navigate ${buzz} by combining deep technical skill with clear, strategic thinking.`;
      const achieveLine = achievePhrase
        ? `\n\n${connector} this is not theoretical. ${achievePhrase.charAt(0).toUpperCase() + achievePhrase.slice(1)} -- a direct result of applying these strengths where they matter most.`
        : `\n\n${connector} ${p.my} value goes beyond individual contributions. ${p.I} build systems, mentor teams, and create frameworks that improve ${impact2} long after the initial project is complete.`;
      const goalLine = goalsPhrase
        ? `\n\n${p.iAm.charAt(0).toUpperCase() + p.iAm.slice(1)} focused on ${goalsPhrase.charAt(0).toLowerCase() + goalsPhrase.slice(1)}. ${closer}`
        : `\n\n${closer}`;
      return hook + '\n\n' + body + achieveLine + goalLine;
    },

    // Template 2: The promise statement
    () => {
      const hook = `${p.I} help ${industry} organizations turn ${domain} challenges into competitive advantages.`;
      const body = yrs
        ? `As a ${descriptor} ${role} with ${yrs} of experience, ${p.my} value proposition is straightforward: ${p.I} combine expertise in ${skillsPhrase} with a relentless focus on ${impact} to deliver outcomes that are ${emphatic}. ${p.I} do not just solve today's problems -- ${p.I} build the foundation for tomorrow's growth.`
        : `As a ${descriptor} ${role}, ${p.my} value proposition is straightforward: ${p.I} combine expertise in ${skillsPhrase} with a relentless focus on ${impact} to deliver outcomes that are ${emphatic}.`;
      const achieveLine = achievePhrase
        ? `\n\n${achievePhrase.charAt(0).toUpperCase() + achievePhrase.slice(1)} -- a testament to this approach in action.`
        : `\n\nThis philosophy has driven consistent results across ${impact} and ${impact2}, earning the trust of stakeholders at every level.`;
      const goalLine = goalsPhrase
        ? `\n\n${p.iAm.charAt(0).toUpperCase() + p.iAm.slice(1)} currently looking to ${goalsPhrase.charAt(0).toLowerCase() + goalsPhrase.slice(1)}. ${closer}`
        : `\n\n${closer}`;
      return hook + '\n\n' + body + achieveLine + goalLine;
    },

    // Template 3: Three-pillar structure
    () => {
      const pillar1 = sk[0] || 'Strategic thinking';
      const pillar2 = sk[1] || 'Hands-on execution';
      const pillar3 = sk[2] || 'Team leadership';
      const hook = yrs
        ? `${p.my.charAt(0).toUpperCase() + p.my.slice(1)} career as a ${role} rests on three pillars: ${pillar1}, ${pillar2}, and ${pillar3}.`
        : `As a ${role}, ${p.my} work rests on three pillars: ${pillar1}, ${pillar2}, and ${pillar3}.`;
      const body = `In ${industry}, these are not just nice-to-have skills -- they are the foundation for ${emphatic} ${impact}. ${p.I} bring a ${descriptor} perspective to ${domain}, ensuring that every initiative ${p.I} lead is both strategically sound and operationally excellent.`;
      const achieveLine = achievePhrase
        ? `\n\n${connector} when these pillars come together, the results speak for themselves. ${achievePhrase.charAt(0).toUpperCase() + achievePhrase.slice(1)}.`
        : `\n\n${connector} what sets ${p.me} apart is the ability to operate at the intersection of these disciplines -- connecting ${impact} to ${impact2} in ways that create lasting value.`;
      const goalLine = goalsPhrase
        ? `\n\nLooking ahead, ${p.iAm} focused on ${goalsPhrase.charAt(0).toLowerCase() + goalsPhrase.slice(1)}. ${closer}`
        : `\n\n${closer}`;
      return hook + '\n\n' + body + achieveLine + goalLine;
    },

    // Template 4: The differentiator
    () => {
      const verb = pickRandom(indCtx.verbs);
      const hook = `What makes ${p.me} different from other ${role}s? ${p.I} do not just understand ${domain} -- ${p.I} understand what it takes to turn that knowledge into ${emphatic} business outcomes.`;
      const body = yrs
        ? `With ${yrs} in ${industry}, ${p.ive} ${verb} solutions that bridge the gap between strategy and execution. ${p.my.charAt(0).toUpperCase() + p.my.slice(1)} expertise in ${skillsPhrase} means ${p.I} can speak the language of both the boardroom and the frontline, translating complex challenges into clear paths toward ${impact}.`
        : `In ${industry}, ${p.ive} ${verb} solutions that bridge the gap between strategy and execution. ${p.my.charAt(0).toUpperCase() + p.my.slice(1)} expertise in ${skillsPhrase} means ${p.I} can translate complex challenges into clear paths toward ${impact}.`;
      const achieveLine = achievePhrase
        ? `\n\nProof point: ${achievePhrase.charAt(0).toLowerCase() + achievePhrase.slice(1)}. That is what happens when you combine ${descriptor} insight with a bias for action.`
        : `\n\n${connector} ${p.I} approach every challenge with the same ${descriptor} mindset: understand the problem deeply, move quickly, and measure what matters.`;
      const goalLine = goalsPhrase
        ? `\n\n${p.iAm.charAt(0).toUpperCase() + p.iAm.slice(1)} now pursuing ${goalsPhrase.charAt(0).toLowerCase() + goalsPhrase.slice(1)}. ${closer}`
        : `\n\n${closer}`;
      return hook + '\n\n' + body + achieveLine + goalLine;
    },

    // Template 5: Client/stakeholder value lens
    () => {
      const hook = `Organizations hire ${p.me} for one reason: ${p.I} deliver ${emphatic} ${impact} and ${p.I} do it consistently.`;
      const body = yrs
        ? `As a ${descriptor} ${role} with ${yrs} of experience in ${industry}, ${p.I} have developed a sharp focus on what creates real value: ${skillsPhrase} applied at the right moment, in the right way. ${p.I} ${pickRandom(indCtx.verbs)} solutions that do not just look good on paper -- they perform in the real world.`
        : `As a ${descriptor} ${role} in ${industry}, ${p.I} have developed a sharp focus on what creates real value: ${skillsPhrase} applied at the right moment, in the right way.`;
      const achieveLine = achievePhrase
        ? `\n\n${connector} the numbers back it up. ${achievePhrase.charAt(0).toUpperCase() + achievePhrase.slice(1)} -- delivered through a combination of ${buzz} and good old-fashioned execution.`
        : `\n\n${connector} ${p.my} approach is rooted in ${buzz} and shaped by the conviction that ${impact2} is the only metric that truly matters.`;
      const goalLine = goalsPhrase
        ? `\n\nCurrently focused on ${goalsPhrase.charAt(0).toLowerCase() + goalsPhrase.slice(1)}. ${closer}`
        : `\n\n${closer}`;
      return hook + '\n\n' + body + achieveLine + goalLine;
    },

    // Template 6: The "why me" framework
    () => {
      const hook = `In a sea of ${role}s, here is why ${p.I} stand out: ${p.I} combine the technical depth of ${skillsPhrase} with the strategic vision to turn ${domain} into ${emphatic} competitive advantage.`;
      const body = yrs
        ? `${yrs} in ${industry} have taught ${p.me} that skills alone are not enough. What matters is the ability to connect those skills to real outcomes -- ${impact}, ${impact2}, and the kind of progress that compounds over time.`
        : `Working in ${industry} has taught ${p.me} that skills alone are not enough. What matters is the ability to connect those skills to real outcomes -- ${impact} and ${impact2}.`;
      const achieveLine = achievePhrase
        ? `\n\nOne example: ${achievePhrase.charAt(0).toLowerCase() + achievePhrase.slice(1)}. That result captures ${p.my} approach in a nutshell -- ${descriptor}, focused, and outcome-driven.`
        : `\n\n${p.my.charAt(0).toUpperCase() + p.my.slice(1)} ${descriptor} approach means stakeholders get more than a contributor -- they get a partner who is invested in the outcome.`;
      const goalLine = goalsPhrase
        ? `\n\nLooking for opportunities in ${goalsPhrase.charAt(0).toLowerCase() + goalsPhrase.slice(1)}. ${closer}`
        : `\n\n${closer}`;
      return hook + '\n\n' + body + achieveLine + goalLine;
    },
  ];

  return pickRandom(templates)();
}

/* ------------------------------------------------------------------ */
/*  Main generation dispatcher                                         */
/* ------------------------------------------------------------------ */

const styleGenerators: Record<SummaryStyle, (data: FormData) => string> = {
  'Storyteller': generateStoryteller,
  'Achievement-Led': generateAchievementLed,
  'Value Proposition': generateValueProposition,
};

const styleDescriptions: Record<SummaryStyle, string> = {
  'Storyteller': 'Opens with a hook and tells your career story. Great for building connection and standing out.',
  'Achievement-Led': 'Leads with your most impressive results and metrics. Perfect for competitive industries.',
  'Value Proposition': 'Focused on what you bring to the table. Ideal for attracting recruiters and opportunities.',
};

function generateAllSummaries(data: FormData): GeneratedSummary[] {
  const styles: SummaryStyle[] = ['Storyteller', 'Achievement-Led', 'Value Proposition'];
  return styles.map((style) => ({
    style,
    description: styleDescriptions[style],
    text: styleGenerators[style](data),
  }));
}

function regenerateSingle(data: FormData, style: SummaryStyle): string {
  return styleGenerators[style](data);
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const INDUSTRIES = [
  'Technology',
  'Finance',
  'Healthcare',
  'Marketing',
  'Education',
  'Sales',
  'Engineering',
  'Design',
  'Consulting',
  'Other',
];

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function LinkedInSummaryContent() {
  const [role, setRole] = useState('');
  const [industry, setIndustry] = useState('Technology');
  const [yearsOfExperience, setYearsOfExperience] = useState('');
  const [skills, setSkills] = useState('');
  const [achievement, setAchievement] = useState('');
  const [careerGoals, setCareerGoals] = useState('');
  const [tone, setTone] = useState<Tone>('Professional');
  const [perspective, setPerspective] = useState<Perspective>('First');
  const [summaries, setSummaries] = useState<GeneratedSummary[]>([]);
  const [hasGenerated, setHasGenerated] = useState(false);

  const formData: FormData = {
    role: role.trim() || 'Professional',
    industry,
    yearsOfExperience,
    skills,
    achievement,
    careerGoals,
    tone,
    perspective,
  };

  const handleGenerate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!role.trim()) return;
    const result = generateAllSummaries(formData);
    setSummaries(result);
    setHasGenerated(true);
  };

  const handleRegenerate = (index: number) => {
    const updated = [...summaries];
    updated[index] = {
      ...updated[index],
      text: regenerateSingle(formData, updated[index].style),
    };
    setSummaries(updated);
  };

  const styleBadgeColor: Record<SummaryStyle, string> = {
    'Storyteller': 'bg-purple-100 text-purple-700',
    'Achievement-Led': 'bg-amber-100 text-amber-700',
    'Value Proposition': 'bg-teal-100 text-teal-700',
  };

  const inputClass =
    'w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow text-gray-900';
  const labelClass = 'block text-sm font-semibold text-gray-700 mb-1.5';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-stone-100">
      <ToolHero
        title="LinkedIn Summary Generator"
        description="Write a compelling LinkedIn About section that attracts recruiters and showcases your unique value. Get 3 professionally written summaries in seconds."
        category="LinkedIn"
      />

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* ── Input Form ── */}
        <form
          onSubmit={handleGenerate}
          className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Tell us about yourself
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Role / Title */}
            <div className="sm:col-span-2">
              <label htmlFor="role" className={labelClass}>
                Current Role / Title <span className="text-red-500">*</span>
              </label>
              <input
                id="role"
                type="text"
                required
                value={role}
                onChange={(e) => setRole(e.target.value)}
                placeholder="e.g., Product Manager, Data Engineer, UX Designer"
                className={inputClass}
              />
            </div>

            {/* Industry */}
            <div>
              <label htmlFor="industry" className={labelClass}>
                Industry
              </label>
              <select
                id="industry"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                className={inputClass}
              >
                {INDUSTRIES.map((ind) => (
                  <option key={ind} value={ind}>
                    {ind}
                  </option>
                ))}
              </select>
            </div>

            {/* Years of Experience */}
            <div>
              <label htmlFor="yearsOfExperience" className={labelClass}>
                Years of Experience
              </label>
              <select
                id="yearsOfExperience"
                value={yearsOfExperience}
                onChange={(e) => setYearsOfExperience(e.target.value)}
                className={inputClass}
              >
                <option value="">Select...</option>
                <option value="1">1 year</option>
                <option value="2">2 years</option>
                <option value="3">3 years</option>
                <option value="5">5 years</option>
                <option value="7">7 years</option>
                <option value="10">10 years</option>
                <option value="15">15+ years</option>
                <option value="20">20+ years</option>
              </select>
            </div>

            {/* Top Skills */}
            <div className="sm:col-span-2">
              <label htmlFor="skills" className={labelClass}>
                Top 3-5 Skills{' '}
                <span className="text-gray-400 font-normal">(comma-separated)</span>
              </label>
              <input
                id="skills"
                type="text"
                value={skills}
                onChange={(e) => setSkills(e.target.value)}
                placeholder="e.g., Python, Data Analysis, Team Leadership, Agile, SQL"
                className={inputClass}
              />
            </div>

            {/* Biggest Achievement */}
            <div className="sm:col-span-2">
              <label htmlFor="achievement" className={labelClass}>
                Biggest Achievement{' '}
                <span className="text-gray-400 font-normal">(optional -- include numbers if possible)</span>
              </label>
              <input
                id="achievement"
                type="text"
                value={achievement}
                onChange={(e) => setAchievement(e.target.value)}
                placeholder="e.g., Grew revenue by 40% in 12 months, Led a team of 15 engineers"
                className={inputClass}
              />
            </div>

            {/* Career Goals */}
            <div className="sm:col-span-2">
              <label htmlFor="careerGoals" className={labelClass}>
                Career Goals / What You Are Looking For{' '}
                <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                id="careerGoals"
                type="text"
                value={careerGoals}
                onChange={(e) => setCareerGoals(e.target.value)}
                placeholder="e.g., Transition to product leadership, Find a senior IC role at a startup"
                className={inputClass}
              />
            </div>
          </div>

          {/* Tone Radio Buttons */}
          <div className="mt-6">
            <span className={labelClass}>Tone</span>
            <div className="flex flex-wrap gap-3 mt-2">
              {(['Professional', 'Conversational', 'Bold'] as Tone[]).map((t) => (
                <label
                  key={t}
                  className={`flex items-center gap-2 cursor-pointer px-4 py-2.5 rounded-xl border text-sm font-semibold transition-colors ${
                    tone === t
                      ? 'bg-blue-50 border-blue-400 text-blue-700'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="tone"
                    value={t}
                    checked={tone === t}
                    onChange={(e) => setTone(e.target.value as Tone)}
                    className="accent-blue-600"
                  />
                  {t}
                </label>
              ))}
            </div>
          </div>

          {/* Perspective Radio Buttons */}
          <div className="mt-6">
            <span className={labelClass}>Perspective</span>
            <div className="flex flex-wrap gap-3 mt-2">
              {([
                { value: 'First' as Perspective, label: 'First Person (I, my)' },
                { value: 'Third' as Perspective, label: 'Third Person (Name, they)' },
              ]).map(({ value, label }) => (
                <label
                  key={value}
                  className={`flex items-center gap-2 cursor-pointer px-4 py-2.5 rounded-xl border text-sm font-semibold transition-colors ${
                    perspective === value
                      ? 'bg-blue-50 border-blue-400 text-blue-700'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="perspective"
                    value={value}
                    checked={perspective === value}
                    onChange={(e) => setPerspective(e.target.value as Perspective)}
                    className="accent-blue-600"
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>

          {/* Generate Button */}
          <button
            type="submit"
            disabled={!role.trim()}
            className="mt-8 w-full sm:w-auto bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-3 px-10 rounded-xl shadow-lg transition-all text-lg"
          >
            Generate Summary
          </button>
        </form>

        {/* ── Results ── */}
        {hasGenerated && summaries.length > 0 && (
          <section className="mt-12">
            <h2 className="text-2xl font-extrabold text-gray-900 mb-2">
              Your LinkedIn Summaries
            </h2>
            <p className="text-gray-500 mb-8">
              Three styles, one story. Pick the About section that sounds most like you.
            </p>

            <div className="space-y-6">
              {summaries.map((summary, idx) => {
                const charCount = summary.text.length;

                return (
                  <div
                    key={idx}
                    className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:border-blue-200 hover:shadow-xl transition-all"
                  >
                    {/* Header */}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
                      <span
                        className={`inline-block text-xs font-bold px-3 py-1 rounded-full ${styleBadgeColor[summary.style]}`}
                      >
                        {summary.style}
                      </span>
                      <span className="text-sm text-gray-500">
                        {summary.description}
                      </span>
                    </div>

                    {/* Summary Text */}
                    <div className="text-gray-800 leading-relaxed text-base mb-5 whitespace-pre-line">
                      {summary.text}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap items-center gap-3">
                      {/* Character count badge */}
                      <span
                        className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                          charCount > 2600
                            ? 'bg-red-100 text-red-700'
                            : charCount > 2000
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-green-100 text-green-700'
                        }`}
                      >
                        {charCount.toLocaleString()} chars
                      </span>

                      <CopyButton text={summary.text} label="Copy" />

                      <button
                        onClick={() => handleRegenerate(idx)}
                        className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                      >
                        <svg
                          className="w-3.5 h-3.5"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2}
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                          />
                        </svg>
                        Regenerate
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ── Tips Section ── */}
        {hasGenerated && (
          <div className="mt-12 bg-blue-50 border border-blue-200 rounded-2xl p-6 sm:p-8">
            <h3 className="text-lg font-bold text-blue-900 mb-4">
              LinkedIn About Section Best Practices
            </h3>
            <p className="text-blue-800 mb-5 leading-relaxed">
              Your LinkedIn About section is prime real estate. It is one of the most-read sections
              on your profile and a major factor in whether recruiters reach out. Here is how to make
              the most of it:
            </p>
            <ul className="space-y-3 text-sm text-blue-800">
              <li className="flex gap-2">
                <span className="text-blue-500 font-bold mt-0.5 shrink-0">1.</span>
                <span>
                  <strong>Hook them in the first two lines.</strong> LinkedIn truncates your
                  About section after roughly 300 characters. The text above the &ldquo;see
                  more&rdquo; fold is your headline act -- make it count with a compelling opening
                  that makes people want to keep reading.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-blue-500 font-bold mt-0.5 shrink-0">2.</span>
                <span>
                  <strong>Write in first person.</strong> LinkedIn is a professional social network,
                  not a formal resume. First-person (&ldquo;I help teams...&rdquo;) feels more
                  authentic and personable than third-person (&ldquo;John helps teams...&rdquo;).
                  Exception: executive profiles sometimes use third-person effectively.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-blue-500 font-bold mt-0.5 shrink-0">3.</span>
                <span>
                  <strong>Lead with value, not biography.</strong> Recruiters and hiring managers
                  want to know what you can do for them, not just where you have been. Focus on
                  your impact, specialties, and what problems you solve.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-blue-500 font-bold mt-0.5 shrink-0">4.</span>
                <span>
                  <strong>Include keywords naturally.</strong> LinkedIn search is keyword-driven.
                  Weave in your target role titles, core skills, and industry terms throughout your
                  summary so you appear in recruiter searches.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-blue-500 font-bold mt-0.5 shrink-0">5.</span>
                <span>
                  <strong>Use white space and short paragraphs.</strong> A wall of text is a
                  conversion killer. Break your summary into 3-4 short paragraphs or use line
                  breaks to make it scannable. Aim for 1,500-2,600 characters (the sweet spot
                  for LinkedIn About sections).
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-blue-500 font-bold mt-0.5 shrink-0">6.</span>
                <span>
                  <strong>End with a call to action.</strong> Tell people what to do next -- connect,
                  message you, check out your work. A clear CTA increases inbound messages from
                  recruiters and potential collaborators.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-blue-500 font-bold mt-0.5 shrink-0">7.</span>
                <span>
                  <strong>Add a quantified achievement.</strong> At least one specific, measurable
                  accomplishment makes your entire summary more credible. &ldquo;Grew ARR by
                  150%&rdquo; is infinitely more compelling than &ldquo;experienced in revenue
                  growth.&rdquo;
                </span>
              </li>
            </ul>
            <p className="mt-6 pt-4 border-t border-blue-200 text-sm text-blue-700/70">
              <strong>Sources &amp; Further Reading:</strong>{' '}
              <a href="https://www.linkedin.com/help/linkedin/answer/a522735" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">LinkedIn Help: Profile Best Practices</a>
              {' · '}
              <a href="https://hbr.org/topic/career-planning" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Harvard Business Review: Career Planning</a>
              {' · '}
              <a href="https://www.indeed.com/career-advice/resumes-cover-letters/linkedin-summary-examples" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Indeed: LinkedIn Summary Examples</a>
            </p>
          </div>
        )}

        {/* ── CTA ── */}
        <ToolCTA
          headline="Your summary gets them interested. Your resume seals the deal."
          description="Create a tailored, ATS-optimized resume that matches your LinkedIn story in minutes with Superpower Resume."
          buttonText="Build My Resume Free"
          buttonHref="https://app.superpowerresume.com/sign-up"
        />
      </div>
    </div>
  );
}
